import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  styled,
  Chip,
  Grid,
  IconButton,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  ErrorOutlineSharp,
  Description,
  FilterList,
  CheckCircle,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "";
// Declare window.fs type
declare global {
  interface Window {
    fs: {
      readFile: (path: string) => Promise<Uint8Array>;
    };
  }
}
const columnConfig = {
  engineering: [
    { key: "OrderReleased", label: "Order Released", defaultVisible: false },
    { key: "BOMReleased", label: "BOM Released", defaultVisible: false },
    {
      key: "EngineeringComplete",
      label: "Engineering Complete",
      defaultVisible: false,
    },
    {
      key: "DraftingComplete",
      label: "Drafting Complete",
      defaultVisible: false,
    },
    { key: "MFGEngComplete", label: "MFG Eng Complete", defaultVisible: true },
  ],
  operations: [
    {
      key: "PlanningComplete",
      label: "Planning Complete",
      defaultVisible: false,
    },
    { key: "OrderCosted", label: "Order Costed", defaultVisible: false },
    { key: "OrderSequenced", label: "Order Sequenced", defaultVisible: true },
  ],
  shipping: [
    { key: "ReadyToShip", label: "Ready To Ship", defaultVisible: false },
    { key: "OrderShipped", label: "Order Shipped", defaultVisible: true },
  ],
};
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: 0,
  height: "100%",
  textAlign: "center",
  "& > div": {
    height: "100%",
    minHeight: "30px",
  },
  borderRight: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderRight: "none",
  },
}));

const HeaderCell = styled(TableCell)(
  ({
    theme,
    section,
  }: {
    theme: any;
    section: "engineering" | "operations" | "shipping" | "SOI";
  }) => {
    return {
      position: "sticky",
      background: "#F8F8F8",
      borderBottom: `1px solid  `,
      border: `1px solid rgb(213, 213, 213)  `,
      textAlign: "center",
      fontWeight: "bold",
      outline: "solid 1px rgb(213, 213, 213)",
    };
  }
);

// Interfaces
interface BusinessUnit {
  [key: string]: string;
}
interface ProductLine {
  [key: string]: string;
}
interface ProductFamily {
  [key: string]: string;
}
interface Stage {
  [key: string]: string;
}
interface OrderStatus {
  statusName: string;
  status: boolean;
  delay: boolean;
  daysDelayed: string;
  data: string;
}
interface OrderDetail {
  esoi: string;
  status: OrderStatus[];
  orderStatus: string;
  so: string;
  schedDate: string;
  draftingStart: string;
  dueDate: string;
  description: string;
  dsStatus: string;
  bom: string;
  holdStatus: string;
  pcl: string;
  plant: string;
  businessUnit: string;
  productLine: string;
  productFamily?: string;
  stage?: string;
}
interface Order {
  esoi: string;
  status: OrderStatus[];
}

interface OrdersResponse {
  orders: Order[];
  ordersCount: number;
}

interface FilterParams {
  ESOI?: string;
  BusinessUnit?: string;
  ProductLine?: string;
  ProductFamily?: string;
  Stage?: string;
  StartDate?: string;
  EndDate?: string;
  page: number;
  itemsPerPage: number;
}
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

// Helper function to format dates for API
function formatDateForApi(date: string | null): string | undefined {
  if (!date) return undefined;
  return new Date(date).toISOString();
}

const OrderTracker = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterParams>({
    page: parseInt(searchParams.get("page") || "1"),
    itemsPerPage: parseInt(searchParams.get("itemsPerPage") || "50"),
    ProductFamily: searchParams.get("ProductFamily") || "",
    ESOI: searchParams.get("ESOI") || "",
    BusinessUnit: searchParams.get("BusinessUnit") || "",
    ProductLine: searchParams.get("ProductLine") || "",
    Stage: searchParams.get("Stage") || "",
    StartDate: searchParams.get("StartDate") || undefined,
    EndDate: searchParams.get("EndDate") || undefined,
  });

  const [startDate, setStartDate] = useState<string>(
    searchParams.get("StartDate") || ""
  );
  const [endDate, setEndDate] = useState<string>(
    searchParams.get("EndDate") || ""
  );
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit>({});
  const [productLines, setProductLines] = useState<string[]>([]); // Nuevo estado para Product Lines
  const [productFamilies, setProductFamilies] = useState<string[]>([]); // Nuevo estado para Product Families
  const [stages, setStages] = useState<Stage>({});
  const [totalCount, setTotalCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  // Agregar nuevo estado para las secciones expandidas
  const [expandedSections, setExpandedSections] = useState({
    engineering: false,
    operations: false,
    shipping: false,
  });
  const [filtersLoading, setFiltersLoading] = useState({
    businessUnits: false,
    productLines: false,
    productFamilies: false,
    stages: false,
  });

  // NEW: memo para saber si hay catálogos cargando
  const isFiltersLoading =
    filtersLoading.businessUnits ||
    filtersLoading.productLines ||
    filtersLoading.productFamilies ||
    filtersLoading.stages;

  // NEW: fetchOrders con cancelación
  const abortRef = React.useRef<AbortController | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const fetchOrders = React.useCallback(async () => {
    try {
      abortRef.current?.abort();                   // cancela el anterior
      const controller = new AbortController();
      abortRef.current = controller;

      // Si ya hay datos y no es la carga inicial, mostrar refreshing
      if (initialLoadComplete) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const queryParams = {
        page: filters.page,
        itemsPerPage: filters.itemsPerPage,
        ...(filters.ESOI && { ESOI: filters.ESOI }),
        ...(filters.BusinessUnit && { BusinessUnit: filters.BusinessUnit }),
        ...(filters.ProductLine && { ProductLine: filters.ProductLine }),
        ...(filters.ProductFamily && { ProductFamily: filters.ProductFamily }),
        ...(filters.Stage && { Stage: filters.Stage }),
        ...(filters.StartDate && { StartDate: filters.StartDate }),
        ...(filters.EndDate && { EndDate: filters.EndDate }),
      };

      const response = await axios.get<OrdersResponse>(
        `${API_URL}/OrdersTracker/orders`,
        {
          params: queryParams,
          headers: { Accept: "application/json" },
          signal: controller.signal,              // importante
        }
      );

      setOrders(response.data.orders);
      setTotalCount(response.data.ordersCount);
    } catch (err: any) {
      if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API_URL, filters, initialLoadComplete]);

  // NEW: ÚNICO efecto que dispara orders con debounce
  const debounceRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!initialLoadComplete) return;       // espera carga inicial
    if (isFiltersLoading) return;           // espera catálogos

    // debounce 300ms para evitar ráfagas
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchOrders();
    }, 300) as unknown as number;

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [initialLoadComplete, isFiltersLoading, filters.page, filters.itemsPerPage, filters.ESOI, filters.BusinessUnit, filters.ProductLine, filters.ProductFamily, filters.Stage, filters.StartDate, filters.EndDate]);

  // Función para alternar la visibilidad de las secciones
  const toggleSection = (
    section: "engineering" | "operations" | "shipping"
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Función para determinar si una columna debe mostrarse
  const isColumnVisible = (
    section: "engineering" | "operations" | "shipping",
    column: { defaultVisible: boolean }
  ) => {
    return column.defaultVisible || expandedSections[section];
  };

  // Fetch Business Units
  const fetchBusinessUnits = async () => {
    try {
      setFiltersLoading((prev) => ({ ...prev, businessUnits: true }));
      const response = await axios.get<BusinessUnit>(
        `${API_URL}/OrdersTracker/businessunits`
      );
      setBusinessUnits(response.data);
    } catch (err) {
      console.error("Error fetching business units:", err);
    } finally {
      setFiltersLoading((prev) => ({ ...prev, businessUnits: false }));
    }
  };

  // Fetch Product Families - Puede recibir businessUnit como parámetro opcional
  const fetchProductFamilies = async (businessUnit?: string) => {
    try {
      setFiltersLoading((prev) => ({ ...prev, productFamilies: true }));
      let url = `${API_URL}/OrdersTracker/productfamilies`;
      
      // Si hay un businessUnit seleccionado, enviarlo como parámetro
      if (businessUnit) {
        url += `?businessUnit=${encodeURIComponent(businessUnit)}`;
      }
      
      const response = await axios.get<string[]>(url);
      setProductFamilies(response.data);
    } catch (err) {
      console.error("Error fetching product families:", err);
    } finally {
      setFiltersLoading((prev) => ({ ...prev, productFamilies: false }));
    }
  };

  // Fetch Product Lines - Puede recibir productFamily como parámetro opcional
  const fetchProductLines = async (productFamily?: string) => {
    try {
      setFiltersLoading((prev) => ({ ...prev, productLines: true }));
      let url = `${API_URL}/OrdersTracker/productlines`;
      
      // Si hay un productFamily seleccionado, enviarlo como parámetro
      if (productFamily) {
        url += `?productFamily=${encodeURIComponent(productFamily)}`;
      }
      
      const response = await axios.get<string[]>(url);
      setProductLines(response.data);
    } catch (err) {
      console.error("Error fetching product lines:", err);
    } finally {
      setFiltersLoading((prev) => ({ ...prev, productLines: false }));
    }
  };

  const fetchStages = async () => {
    try {
      setFiltersLoading((prev) => ({ ...prev, stages: true }));
      const response = await axios.get<Stage>(
        `${API_URL}/OrdersTracker/stages`
      );
      setStages(response.data);
    } catch (err) {
      console.error("Error fetching stages:", err);
    } finally {
      setFiltersLoading((prev) => ({ ...prev, stages: false }));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();

    // Only add non-empty values to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString());
      }
    });

    // Update URL without causing a page reload
    setSearchParams(params);
  }, [filters, setSearchParams]);

  useEffect(() => {
    fetchBusinessUnits();
    fetchStages();
    fetchProductFamilies(); // Cargar todas las familias de productos inicialmente
    fetchProductLines(); // Cargar todas las líneas de productos inicialmente
  }, []);

  // Actualizar ProductFamilies cuando cambie BusinessUnit
  useEffect(() => {
    if (filters.BusinessUnit) {
      fetchProductFamilies(filters.BusinessUnit);
      // Limpiar filtro de ProductFamily si se cambia BusinessUnit
      if (filters.ProductFamily) {
        setFilters(prev => ({...prev, ProductFamily: "", ProductLine: ""}));
      }
    } else {
      fetchProductFamilies(); // Sin parámetro para obtener todos
    }
  }, [filters.BusinessUnit]);

  // Actualizar ProductLines cuando cambie ProductFamily
  useEffect(() => {
    if (filters.ProductFamily) {
      fetchProductLines(filters.ProductFamily);
      // Limpiar filtro de ProductLine si se cambia ProductFamily
      if (filters.ProductLine) {
        setFilters(prev => ({...prev, ProductLine: ""}));
      }
    } else {
      fetchProductLines(); // Sin parámetro para obtener todos
    }
  }, [filters.ProductFamily]);

  // Set initialLoadComplete when all catalogs are loaded
  useEffect(() => {
    if (!isFiltersLoading && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [isFiltersLoading, initialLoadComplete]);

  // Update filters when both dates are selected
  useEffect(() => {
    if (startDate && endDate) {
      setFilters((prev) => ({
        ...prev,
        StartDate: formatDateForApi(startDate),
        EndDate: formatDateForApi(endDate),
        page: 1, // Reset to page 1 when changing date filters
      }));
    } else if (!startDate && !endDate) {
      // Clear date filters if both dates are empty
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.StartDate;
        delete newFilters.EndDate;
        return newFilters;
      });
    }
  }, [startDate, endDate]);

  const handleOrderClick = (order: any) => {
    console.log(order);
    setSelectedOrder(order);
    setDrawerOpen(true);
  };

  const handleFilterChange = (field: keyof FilterParams, value: any) => {
    setFilters((prev) => {
      // Crear nuevos filtros
      const newFilters = {
        ...prev,
        [field]: value,
        // Reset to page 1 when changing filters
        ...(field !== "page" && field !== "itemsPerPage" ? { page: 1 } : {}),
      };
      // Remove empty values
      Object.keys(newFilters).forEach((key) => {
        if (!newFilters[key as keyof FilterParams]) {
          delete newFilters[key as keyof FilterParams];
        }
      });

      return newFilters;
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    handleFilterChange("page", newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFilterChange("itemsPerPage", parseInt(event.target.value, 10));
    handleFilterChange("page", 1);
  };

  // Handlers for date inputs
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  // Reset all filters
  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setFilters({
      page: 1,
      itemsPerPage: 50,
    });
  };

  const getStatusCell = (status: any) => {
    if (status.status) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-[#F1F8E9] text-green-600">
          <img src="/checks.svg" alt="Success Icon" />
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-full h-full text-red-600 gap-1">
          <img src="/errors.svg" alt="Success Icon" />
        </div>
      );
    }
  };

  const getStatusByName = (
    statuses: OrderStatus[],
    name: string
  ): OrderStatus => {
    return (
      statuses.find((s) => s.statusName === name) || {
        statusName: name,
        status: false,
        delay: false,
        daysDelayed: "",
        data: "",
      }
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" component="h1">
          Order Tracker
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Description />}
            disabled={true}
          >
            Build Report
          </Button>
          <Button variant="outlined" startIcon={<FilterList />} disabled={true}>
            Statistics
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            label="Item/SOI"
            variant="outlined"
            size="small"
            value={filters.ESOI || ""}
            onChange={(e) => handleFilterChange("ESOI", e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Business Units</InputLabel>
            <Select
              label="Business Units"
              value={filters.BusinessUnit || ""}
              onChange={(e) =>
                handleFilterChange("BusinessUnit", e.target.value)
              }
              disabled={filtersLoading.businessUnits}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(businessUnits).map(([id, name]) => (
                <MenuItem key={id} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Nuevo filtro para Product Family */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Product Family</InputLabel>
            <Select
              label="Product Family"
              value={filters.ProductFamily || ""}
              onChange={(e) =>
                handleFilterChange("ProductFamily", e.target.value)
              }
              disabled={filtersLoading.productFamilies}
            >
              <MenuItem value="">All Families</MenuItem>
              {productFamilies.map((family) => (
                <MenuItem key={family} value={family}>
                  {family}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Nuevo filtro para Product Lines */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Product Line</InputLabel>
            <Select
              label="Product Line"
              value={filters.ProductLine || ""}
              onChange={(e) =>
                handleFilterChange("ProductLine", e.target.value)
              }
              disabled={filtersLoading.productLines}
            >
              <MenuItem value="">All Lines</MenuItem>
              {productLines.map((line) => (
                <MenuItem key={line} value={line}>
                  {line}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Stage</InputLabel>
            <Select
              label="Stage"
              value={filters.Stage || ""}
              onChange={(e) => handleFilterChange("Stage", e.target.value)}
              disabled={filtersLoading.stages}
            >
              <MenuItem value="">All Stages</MenuItem>
              {Object.entries(stages).map(([id, name]) => (
                <MenuItem key={id} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Range Inputs */}
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={handleStartDateChange}
            sx={{ width: 170 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={handleEndDateChange}
            sx={{ width: 170 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate, // Prevent selecting dates before start date
            }}
          />
          <Button variant="outlined" onClick={resetFilters} size="small">
            Reset All
          </Button>
        
        </Box>
      </Paper>

             {/* Table */}
       <TableContainer
         component={Paper}
         sx={(theme) => ({
           maxHeight: {
             xs: "400px",
             md: "calc(100vh - 300px)",
             lg: "calc(100vh - 280px)",
             xl: "calc(100vh - 260px)",
           },
           [`@media (min-width: 1536px)`]: {
             maxHeight: "calc(100vh - 300px)",
           },
           "& .MuiTableHead-root": {
             position: "sticky",
             top: -1,
             zIndex: 1,
             backgroundColor: "#fff",
           },
         })}
       >
         {loading ? (
           <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
             <CircularProgress />
           </Box>
                   ) : orders && orders.length === 0 ? (
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column",
              justifyContent: "center", 
              alignItems: "center", 
              p: 6,
              textAlign: "center"
            }}>
              <Description sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No orders found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No orders match the applied filters.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting the filters or search with different criteria.
              </Typography>
            </Box>
                   ) : (
            <Box sx={{ position: "relative" }}>
              <Table size="small">
             <TableHead>
               <TableRow>
                 <HeaderCell theme={undefined} section={"SOI"}>
                   Item/SOI
                 </HeaderCell>
                 <HeaderCell
                   section="engineering"
                   colSpan={
                     columnConfig.engineering.filter((col) =>
                       isColumnVisible("engineering", col)
                     ).length
                   }
                   theme={undefined}
                 >
                   <Box
                     sx={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                     }}
                   >
                     Engineering/Design
                     <IconButton
                       size="small"
                       onClick={() => toggleSection("engineering")}
                       sx={{ ml: 1 }}
                     >
                       {expandedSections.engineering ? (
                         <RemoveIcon />
                       ) : (
                         <AddIcon />
                       )}
                     </IconButton>
                   </Box>
                 </HeaderCell>
                 <HeaderCell
                   section="operations"
                   colSpan={
                     columnConfig.operations.filter((col) =>
                       isColumnVisible("operations", col)
                     ).length
                   }
                   theme={undefined}
                 >
                   <Box
                     sx={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                     }}
                   >
                     Operations Back Office
                     <IconButton
                       size="small"
                       onClick={() => toggleSection("operations")}
                       sx={{ ml: 1 }}
                     >
                       {expandedSections.operations ? (
                         <RemoveIcon />
                       ) : (
                         <AddIcon />
                       )}
                     </IconButton>
                   </Box>
                 </HeaderCell>
                 <HeaderCell
                   section="shipping"
                   colSpan={
                     columnConfig.shipping.filter((col) =>
                       isColumnVisible("shipping", col)
                     ).length
                   }
                   theme={undefined}
                 >
                   <Box
                     sx={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                     }}
                   >
                     Shipping
                     <IconButton
                       size="small"
                       onClick={() => toggleSection("shipping")}
                       sx={{ ml: 1 }}
                     >
                       {expandedSections.shipping ? <RemoveIcon /> : <AddIcon />}
                     </IconButton>
                   </Box>
                 </HeaderCell>
               </TableRow>
               {(expandedSections.engineering ||
                 expandedSections.operations ||
                 expandedSections.shipping) && (
                 <TableRow>
                   <TableCell></TableCell>
                   {/* Engineering/Design Columns */}
                   {columnConfig.engineering.map(
                     (col) =>
                       isColumnVisible("engineering", col) && (
                         <StyledTableCell key={col.key}>
                           {col.label}
                         </StyledTableCell>
                       )
                   )}
                   {/* Operations Columns */}
                   {columnConfig.operations.map(
                     (col) =>
                       isColumnVisible("operations", col) && (
                         <StyledTableCell key={col.key}>
                           {col.label}
                         </StyledTableCell>
                       )
                   )}
                   {/* Shipping Columns */}
                   {columnConfig.shipping.map(
                     (col) =>
                       isColumnVisible("shipping", col) && (
                         <StyledTableCell key={col.key}>
                           {col.label}
                         </StyledTableCell>
                       )
                   )}
                 </TableRow>
               )}
             </TableHead>
             <TableBody>
               {orders && orders.map((order) => (
                 <TableRow key={order.esoi} hover>
                   <TableCell
                     sx={{
                       pl: 2,
                       cursor: "pointer",
                       "&:hover": {
                         textDecoration: "underline",
                         color: "primary.main",
                       },
                     }}
                     onClick={() => handleOrderClick(order)}
                   >
                     {order.esoi}
                   </TableCell>
                   {/* Engineering/Design Cells */}
                   {columnConfig.engineering.map(
                     (col) =>
                       isColumnVisible("engineering", col) && (
                         <StyledTableCell key={col.key}>
                           {getStatusCell(
                             getStatusByName(order.status, col.key)
                           )}
                         </StyledTableCell>
                       )
                   )}
                   {/* Operations Cells */}
                   {columnConfig.operations.map(
                     (col) =>
                       isColumnVisible("operations", col) && (
                         <StyledTableCell key={col.key}>
                           {getStatusCell(
                             getStatusByName(order.status, col.key)
                           )}
                         </StyledTableCell>
                       )
                   )}
                   {/* Shipping Cells */}
                   {columnConfig.shipping.map(
                     (col) =>
                       isColumnVisible("shipping", col) && (
                         <StyledTableCell key={col.key}>
                           {getStatusCell(
                             getStatusByName(order.status, col.key)
                           )}
                         </StyledTableCell>
                       )
                   )}
                 </TableRow>
               ))}
                           </TableBody>
            </Table>
                             {refreshing && (
                 <Box
                   sx={{
                     position: "fixed",
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     backgroundColor: "rgba(255, 255, 255, 0.8)",
                     display: "flex",
                     justifyContent: "center",
                     alignItems: "center",
                     zIndex: 9999,
                   }}
                 >
                   <Box sx={{ 
                     textAlign: "center",
                     backgroundColor: "white",
                     padding: 3,
                     borderRadius: 2,
                     boxShadow: 3,
                     border: "1px solid rgba(0, 0, 0, 0.1)"
                   }}>
                     <CircularProgress size={40} />
                     <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                       Getting results...
                     </Typography>
                   </Box>
                 </Box>
               )}
            </Box>
          )}
        </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={filters.page - 1}
        rowsPerPage={filters.itemsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        showFirstButton={true}
        showLastButton={true}
      />
      {/* Drawer Component */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "400px",
            p: 3,
          },
        }}
      >
        {selectedOrder && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6">ORDER DETAIL</Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <IconButton />
              </IconButton>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              SO #: {selectedOrder.so || "Pending"} ESOI#:{" "}
              {selectedOrder.esoi || "Pending"}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Building Week
                </Typography>
                <Typography>
                  {selectedOrder.schedDate
                    ? formatDate(selectedOrder.schedDate)
                    : "Pending"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Drafting Start
                </Typography>
                <Typography>{selectedOrder.draftingStart || "--"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Due date
                </Typography>
                <Typography>{selectedOrder.dueDate || "--"}</Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography>ORDER STATUS</Typography>
                <Typography color="text.secondary">
                  {selectedOrder.orderStatus || "--"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>DS Status</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.dsStatus || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>BOM</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.bom || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Hold Status</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.holdStatus || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>PCL</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.pcl || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Plant</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.plant || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Business Unit</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.businessUnit || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Product Line</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.productLine || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Product Family</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.productFamily || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Stage</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.stage || "--"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Order Status</Typography>
                  <Typography color="text.secondary">
                    {selectedOrder.orderStatus || "--"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default OrderTracker;